import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Header from './Header';
import styles from './AncientStyleJueju.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

const AncientStyleJueju = () => {
  // ===== UI state (copied from Xiucai style) =====
  const [isFiveWordLine, setIsFiveWordLine] = useState(true);
  const [poemUnits, setPoemUnits] = useState(Array(20).fill(''));
  const [submittedPoem, setSubmittedPoem] = useState('');

  // placeholder typing effect (safe UI-only)
  const [placeholderText, setPlaceholderText] = useState('');
  const placeholderContent = 'Type here...';

  // ===== Same result handling style as Assess.js =====
  const [feedback, setFeedback] = useState('');
  const [pingZeFeedback, setPingZeFeedback] = useState('');
  const [modelAssessment, setModelAssessment] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isValid, setIsValid] = useState(null);

  useEffect(() => {
    if (!poemUnits[0]) {
      let index = 0;
      const interval = setInterval(() => {
        setPlaceholderText(placeholderContent.substring(0, index));
        index = (index + 1) % (placeholderContent.length + 1);
      }, 200);
      return () => clearInterval(interval);
    }
  }, [poemUnits]);

  const handleToggle = () => {
    setIsFiveWordLine((prev) => !prev);
    setPoemUnits(Array(!isFiveWordLine ? 20 : 28).fill(''));
  };

  const handleInputChange = (index, value) => {
    const next = [...poemUnits];
    next[index] = value;
    setPoemUnits(next);
  };

  // Xiucai-style 4 line grid
  const renderPoemInputs = () => {
    const lineUnits = isFiveWordLine ? 5 : 7;
    const totalLines = 4;

    const lineBreaks = [];
    for (let i = 0; i < totalLines; i++) {
      const units = [];
      for (let j = 0; j < lineUnits; j++) {
        units.push(
          <input
            key={i * lineUnits + j}
            type="text"
            value={poemUnits[i * lineUnits + j]}
            onChange={(e) => handleInputChange(i * lineUnits + j, e.target.value)}
            className={styles.unitBox}
            placeholder={j === 0 && i === 0 ? placeholderText : ''}
            onFocus={() => j === 0 && i === 0 && setPlaceholderText('')}
          />
        );
        // same gaps pattern as BuildPoem
        if (isFiveWordLine && j === 1) {
          units.push(<div key={`gap-${i}-${j}`} className={styles.gap}></div>);
        } else if (!isFiveWordLine && (j === 1 || j === 3)) {
          units.push(<div key={`gap-${i}-${j}`} className={styles.gap}></div>);
        }
      }
      lineBreaks.push(
        <div key={`line-${i}`} className={styles.line}>
          {units}
        </div>
      );
    }
    return lineBreaks;
  };

  const formatPoem = () => {
    const lineUnits = isFiveWordLine ? 5 : 7;
    const totalLines = 4;
    let poem = '';

    for (let i = 0; i < totalLines; i++) {
      const line = poemUnits.slice(i * lineUnits, (i + 1) * lineUnits).join(' ');
      poem += line + '\n';
    }
    return poem.trim();
  };

  // IMPORTANT: Submit logic must behave like Assess.js (no backend change)
  const handleSubmit = async () => {
    const poemText = submittedPoem.trim() || formatPoem();

    if (!poemText) {
      setError('Please enter your poem before submitting.');
      return;
    }

    setLoading(true);
    setFeedback('');
    setPingZeFeedback('');
    setModelAssessment('');
    setError(null);
    setIsValid(null);

    // Ancient Style Jueju = unregulated poem => same mapping as Assess.js => level = xiucai
    const endpoint = '/assess-poem';
    const payload = {
      poem: poemText,
      user_level: 'guest',
      level: 'xiucai',
    };

    try {
      const response = await axios.post(`${API_URL}${endpoint}`, payload, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      });

      setIsValid(response.data.is_valid);
      setFeedback(response.data.feedback);
      setPingZeFeedback(response.data.ping_ze_feedback || '');
      setModelAssessment(response.data.model_assessment || '');
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred while processing your submission.');
    }

    setLoading(false);
  };

  const handleClear = () => {
    setIsFiveWordLine(true);
    setPoemUnits(Array(20).fill(''));
    setSubmittedPoem('');
    setFeedback('');
    setPingZeFeedback('');
    setModelAssessment('');
    setError(null);
    setLoading(false);
    setIsValid(null);
  };

  return (
    <div>
      <Header />
      <div className={styles.pageOffset}>

        <div className={styles.container}>
            <h2>Ancient Style Jueju</h2>

            <div className={styles.toggleContainer}>
            <span>{isFiveWordLine ? '5-word per line' : '7-word per line'}</span>
            <label className={styles.switch}>
                <input type="checkbox" checked={!isFiveWordLine} onChange={handleToggle} />
                <span className={`${styles.slider} ${styles.round}`}></span>
            </label>
            </div>

            <p>Type in unit by unit:</p>
            {renderPoemInputs()}

            <p>OR</p>
            <p>Drop your whole poem</p>

            <textarea
            placeholder={
                "Enter your poem here...\n\nNote: Please remove all punctuation from your poem including apostrophes!"
            }
            value={submittedPoem}
            onChange={(e) => setSubmittedPoem(e.target.value)}
            className={styles.textArea}
            />

            <div className={styles.submitButtonsContainer}>
            <button
                onClick={handleSubmit}
                disabled={loading}
                className={`${styles.aiButton} ${styles.button}`}
            >
                {loading ? 'Submitting...' : 'Submit'}
            </button>

            <button
                onClick={handleClear}
                disabled={loading}
                className={`${styles.button} ${styles.resetButton}`}
            >
                Clear
            </button>
            </div>

            {loading && <div className={styles.loader}></div>}
            {error && <div className={styles.errorMessage}>{error}</div>}

            {feedback && (
            <div className={`${styles.assessmentResult} ${isValid === false ? styles.invalidFeedback : ''}`}>
                <h3>{isValid === false ? 'Invalid Submission' : 'Feedback'}</h3>
                <p>{feedback}</p>
            </div>
            )}

            {modelAssessment && isValid && (
            <div className={styles.modelAssessment}>
                <h4>Model Assessment</h4>
                <p className={styles.generatedText}>{modelAssessment}</p>
            </div>
            )}

            {pingZeFeedback && (
            <div className={styles.pingZeFeedback}>
                <h4>Ping/Ze Feedback</h4>
                {pingZeFeedback.split('\n').map((line, idx) => (
                <p key={idx}>{line}</p>
                ))}
            </div>
            )}
        </div>
        </div>
    </div>
  );
};

export default AncientStyleJueju;
